import { cameraControls, render } from '@/threejs/core';
import { isOrthographic, switchCamera } from '@/threejs/core/camera';
import * as THREE from 'three';
import { computed, ref } from 'vue';

export const useCameraControls = (sendCameraPosition = null) => {
  const isOrthographicRef = ref(false);
  const cameraPositionRef = ref({ x: 0, y: 0, z: 0 });

  const isOrthographicView = computed(() => isOrthographicRef.value);

  /**
   * Rotate camera controls by given theta angle
   * @param {number} theta - Angle in degrees
   */
  const rotateControls = (theta) => {
    cameraControls.rotate(theta * THREE.MathUtils.DEG2RAD, 0, true);
  };

  /**
   * Switch to isometric (orthographic) view
   * @param {HTMLElement} canvasContainer - Canvas container element
   */
  const isometricView = (canvasContainer) => {
    switchCamera(canvasContainer, true);
    render();
  };

  /**
   * Switch to perspective view
   * @param {HTMLElement} canvasContainer - Canvas container element
   */
  const perspectiveView = (canvasContainer) => {
    switchCamera(canvasContainer, false);
    render();
  };

  /**
   * Snap camera to front view (only in orthographic mode)
   */
  const snapToFrontView = () => {
    if (!isOrthographic()) return;
    const pos = new THREE.Vector3(0, 0, 10);
    const target = new THREE.Vector3(0, 0, 0);
    cameraControls.setLookAt(pos.x, pos.y, pos.z, target.x, target.y, target.z, true);
    render();
  };

  /**
   * Snap camera to top view (only in orthographic mode)
   */
  const snapToTopView = () => {
    if (!isOrthographic()) return;
    const pos = new THREE.Vector3(0, 10, 0);
    const target = new THREE.Vector3(0, 0, 0);
    cameraControls.setLookAt(pos.x, pos.y, pos.z, target.x, target.y, target.z, true);
    render();
  };

  /**
   * Snap camera to side view (only in orthographic mode)
   * @param {boolean} left - Whether to show left side view (true) or right side view (false)
   */
  const snapToSideView = (left = false) => {
    if (!isOrthographic()) return;
    const pos = new THREE.Vector3(left ? -10 : 10, 0, 0);
    const target = new THREE.Vector3(0, 0, 0);
    cameraControls.setLookAt(pos.x, pos.y, pos.z, target.x, target.y, target.z, true);
    render();
  };

  /**
   * Update camera position display values
   */
  const updateCameraPosition = () => {
    if (cameraControls?.camera) {
      cameraPositionRef.value.x = cameraControls.camera.position.x.toFixed(2);
      cameraPositionRef.value.y = cameraControls.camera.position.y.toFixed(2);
      cameraPositionRef.value.z = cameraControls.camera.position.z.toFixed(2);
    }
  };

  /**
   * Update orthographic view state
   */
  const updateOrthographicState = () => {
    isOrthographicRef.value = isOrthographic();
  };

  /**
   * Initialize camera controls listeners
   */
  const initCameraControls = () => {
    // Set initial state
    updateOrthographicState();
    updateCameraPosition();

    // Add camera change listener
    const handleCameraChange = () => {
      updateOrthographicState();
    };

    // Add camera controls update listener
    let cameraUpdateListener = null;
    if (cameraControls) {
      cameraUpdateListener = () => {
        updateCameraPosition();

        // Send camera position update to other users if sendCameraPosition is provided
        if (sendCameraPosition) {
          sendCameraPosition();
        }
      };

      cameraControls.addEventListener('update', cameraUpdateListener);
    }

    window.addEventListener('cameraChanged', handleCameraChange);

    // Return cleanup function
    return () => {
      window.removeEventListener('cameraChanged', handleCameraChange);
      if (cameraControls && cameraUpdateListener) {
        cameraControls.removeEventListener('update', cameraUpdateListener);
      }
    };
  };

  return {
    // State
    isOrthographicView,
    cameraPositionRef,

    // Methods
    rotateControls,
    isometricView,
    perspectiveView,
    snapToFrontView,
    snapToTopView,
    snapToSideView,
    updateOrthographicState,
    updateCameraPosition,
    initCameraControls,
  };
};
